/**
 * Hook para gerenciar atualizações em tempo real do dashboard
 * 
 * Gerencia conexão SSE (Server-Sent Events) ou polling otimizado
 * para atualizar dados do dashboard em tempo real.
 * 
 * @module hooks/useRealtimeData
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ALL_DASHBOARD_QUERY_KEYS } from './useDashboardData';

/**
 * Configurações padrão para tempo real
 */
const DEFAULT_CONFIG = {
    enabled: false, // Desabilitado por padrão
    interval: 30000, // 30 segundos - intervalo de atualização (mínimo: 10s)
    minInterval: 10000, // 10 segundos - intervalo mínimo para não sobrecarregar
    maxInterval: 300000, // 5 minutos - intervalo máximo
    useSSE: false, // Usar SSE quando disponível (futuro)
    sseEndpoint: '/api/dashboard/events', // Endpoint SSE (futuro)
    pauseOnHidden: true, // Pausar quando aba não está visível
    backoffOnError: true, // Aumentar intervalo em caso de erro
};

/**
 * Hook para gerenciar atualizações em tempo real
 * 
 * @param {object} config - Configuração do tempo real
 * @param {boolean} [config.enabled] - Se tempo real está habilitado
 * @param {number} [config.interval] - Intervalo de atualização em ms (padrão: 30000)
 * @param {boolean} [config.useSSE] - Se deve usar SSE (futuro)
 * @param {string} [config.sseEndpoint] - Endpoint SSE (futuro)
 * 
 * @returns {object} Objeto com estado e funções de controle
 * @property {boolean} isRealtimeEnabled - Se tempo real está ativo
 * @property {boolean} isConnected - Se está conectado (SSE) ou ativo (polling)
 * @property {Date|null} lastUpdate - Data da última atualização
 * @property {function} enableRealtime - Função para habilitar tempo real
 * @property {function} disableRealtime - Função para desabilitar tempo real
 * @property {function} toggleRealtime - Função para alternar tempo real
 * 
 * @example
 * ```javascript
 * const { isRealtimeEnabled, enableRealtime, disableRealtime } = useRealtimeData({
 *     enabled: true,
 *     interval: 30000,
 * });
 * ```
 */
export const useRealtimeData = (config = {}) => {
    const {
        enabled = DEFAULT_CONFIG.enabled,
        interval = DEFAULT_CONFIG.interval,
        minInterval = DEFAULT_CONFIG.minInterval,
        maxInterval = DEFAULT_CONFIG.maxInterval,
        useSSE = DEFAULT_CONFIG.useSSE,
        sseEndpoint = DEFAULT_CONFIG.sseEndpoint,
        pauseOnHidden = DEFAULT_CONFIG.pauseOnHidden,
        backoffOnError = DEFAULT_CONFIG.backoffOnError,
    } = config;

    const [isRealtimeEnabled, setIsRealtimeEnabled] = useState(enabled);
    const [isConnected, setIsConnected] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [error, setError] = useState(null);
    const enabledRef = useRef(enabled);
    
    // Sincronizar estado interno com prop enabled apenas quando prop muda externamente
    useEffect(() => {
        if (enabledRef.current !== enabled) {
            enabledRef.current = enabled;
            if (enabled !== isRealtimeEnabled) {
                setIsRealtimeEnabled(enabled);
            }
        }
    }, [enabled, isRealtimeEnabled]);

    const queryClient = useQueryClient();
    const intervalRef = useRef(null);
    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttempts = useRef(0);
    const maxReconnectAttempts = 5;
    const currentIntervalRef = useRef(interval);
    const errorCountRef = useRef(0);
    const isPageVisibleRef = useRef(true);
    const lastRequestTimeRef = useRef(null);

    /**
     * Para polling (função auxiliar sem dependências)
     */
    const stopPollingInternal = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsConnected(false);
    }, []);

    /**
     * Inicia polling (função auxiliar sem dependências)
     */
    const startPollingInternal = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Garantir intervalo mínimo
        const safeInterval = Math.max(interval, minInterval);
        currentIntervalRef.current = safeInterval;

        // Primeira atualização imediata (se página visível) - apenas se não houver dados frescos
        if (isPageVisibleRef.current) {
            // Verificar se há queries stale antes de invalidar
            ALL_DASHBOARD_QUERY_KEYS.forEach((queryKey) => {
                const queryState = queryClient.getQueryState(queryKey);
                // Só invalida se não houver dados ou se os dados estiverem stale
                if (!queryState || queryState.isStale) {
                    queryClient.invalidateQueries({ queryKey });
                }
            });
            setLastUpdate(new Date());
            lastRequestTimeRef.current = Date.now();
        }

        // Configurar intervalo
        intervalRef.current = setInterval(() => {
            // Invalidação dentro do intervalo
            const now = Date.now();
            const minTimeBetweenRequests = 5000;
            
            if (lastRequestTimeRef.current && 
                (now - lastRequestTimeRef.current) < minTimeBetweenRequests) {
                return;
            }

            if (pauseOnHidden && !isPageVisibleRef.current) {
                return;
            }

            // Verificar se há queries stale antes de invalidar
            ALL_DASHBOARD_QUERY_KEYS.forEach((queryKey) => {
                const queryState = queryClient.getQueryState(queryKey);
                // Só invalida se os dados estiverem stale
                if (queryState && queryState.isStale) {
                    queryClient.invalidateQueries({ queryKey });
                }
            });
            setLastUpdate(new Date());
            lastRequestTimeRef.current = now;
            errorCountRef.current = 0;
            
            if (currentIntervalRef.current > interval) {
                currentIntervalRef.current = interval;
            }
        }, currentIntervalRef.current);

        setIsConnected(true);
        setError(null);
    }, [queryClient, interval, minInterval, pauseOnHidden]);

    /**
     * Invalida caches do dashboard para forçar atualização
     * Com throttling para evitar requisições excessivas
     * Só invalida queries que estão stale
     */
    const invalidateDashboardCaches = useCallback(() => {
        // Throttling: não permite mais de uma requisição a cada 5 segundos
        const now = Date.now();
        const minTimeBetweenRequests = 5000; // 5 segundos
        
        if (lastRequestTimeRef.current && 
            (now - lastRequestTimeRef.current) < minTimeBetweenRequests) {
            return; // Ignora se muito recente
        }

        // Verifica se página está visível (se pauseOnHidden estiver ativo)
        if (pauseOnHidden && !isPageVisibleRef.current) {
            return; // Pausa quando aba não está visível
        }

        try {
            // Só invalida queries que estão stale
            let hasStaleQueries = false;
            ALL_DASHBOARD_QUERY_KEYS.forEach((queryKey) => {
                const queryState = queryClient.getQueryState(queryKey);
                // Só invalida se os dados estiverem stale
                if (queryState && queryState.isStale) {
                    queryClient.invalidateQueries({ queryKey });
                    hasStaleQueries = true;
                }
            });
            
            // Só atualiza timestamp se realmente invalidou algo
            if (hasStaleQueries) {
                setLastUpdate(new Date());
                lastRequestTimeRef.current = now;
                errorCountRef.current = 0; // Reset contador de erros em caso de sucesso
                
                // Reset intervalo em caso de sucesso após erro
                if (currentIntervalRef.current > interval) {
                    currentIntervalRef.current = interval;
                    // Reiniciar polling com intervalo normalizado
                    if (isRealtimeEnabled && !useSSE) {
                        stopPollingInternal();
                        setTimeout(() => {
                            if (isRealtimeEnabled && !useSSE) {
                                startPollingInternal();
                            }
                        }, 1000);
                    }
                }
            }
        } catch (err) {
            console.error('Erro ao invalidar caches:', err);
            errorCountRef.current++;
            
            // Backoff exponencial em caso de erro
            if (backoffOnError && errorCountRef.current > 0) {
                const newInterval = Math.min(
                    interval * Math.pow(2, errorCountRef.current),
                    maxInterval
                );
                currentIntervalRef.current = newInterval;
                
                // Reiniciar polling com novo intervalo
                if (isRealtimeEnabled && !useSSE) {
                    stopPollingInternal();
                    setTimeout(() => {
                        if (isRealtimeEnabled && !useSSE) {
                            startPollingInternal();
                        }
                    }, 1000);
                }
            }
        }
    }, [queryClient, pauseOnHidden, backoffOnError, interval, maxInterval, isRealtimeEnabled, useSSE, stopPollingInternal, startPollingInternal]);

    /**
     * Conecta via Server-Sent Events (futuro)
     */
    const connectSSE = useCallback(() => {
        if (!useSSE) return;

        try {
            // TODO: Implementar quando backend suportar SSE
            // const eventSource = new EventSource(sseEndpoint, {
            //     withCredentials: true,
            // });
            // 
            // eventSource.onopen = () => {
            //     setIsConnected(true);
            //     setError(null);
            //     reconnectAttempts.current = 0;
            // };
            // 
            // eventSource.onmessage = (event) => {
            //     const data = JSON.parse(event.data);
            //     if (data.type === 'dashboard_update') {
            //         invalidateDashboardCaches();
            //     }
            // };
            // 
            // eventSource.onerror = (err) => {
            //     setIsConnected(false);
            //     setError('Erro na conexão em tempo real');
            //     
            //     // Tentar reconectar
            //     if (reconnectAttempts.current < maxReconnectAttempts) {
            //         reconnectAttempts.current++;
            //         reconnectTimeoutRef.current = setTimeout(() => {
            //             connectSSE();
            //         }, 5000 * reconnectAttempts.current);
            //     }
            // };
            // 
            // eventSourceRef.current = eventSource;
        } catch (err) {
            console.error('Erro ao conectar SSE:', err);
            setError('Erro ao conectar em tempo real');
        }
    }, [useSSE, sseEndpoint, invalidateDashboardCaches]);

    /**
     * Desconecta SSE
     */
    const disconnectSSE = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        setIsConnected(false);
        reconnectAttempts.current = 0;
    }, []);

    /**
     * Inicia polling otimizado
     */
    const startPolling = startPollingInternal;

    /**
     * Para polling
     */
    const stopPolling = stopPollingInternal;

    /**
     * Habilita tempo real
     */
    const enableRealtime = useCallback(() => {
        setIsRealtimeEnabled(true);
        if (useSSE) {
            connectSSE();
        } else {
            startPolling();
        }
    }, [useSSE, connectSSE, startPolling]);

    /**
     * Desabilita tempo real
     */
    const disableRealtime = useCallback(() => {
        setIsRealtimeEnabled(false);
        if (useSSE) {
            disconnectSSE();
        } else {
            stopPolling();
        }
    }, [useSSE, disconnectSSE, stopPolling]);

    /**
     * Alterna tempo real
     */
    const toggleRealtime = useCallback(() => {
        if (isRealtimeEnabled) {
            disableRealtime();
        } else {
            enableRealtime();
        }
    }, [isRealtimeEnabled, enableRealtime, disableRealtime]);

    /**
     * Efeito para gerenciar conexão quando enabled muda
     * Usa ref para evitar múltiplas inicializações
     */
    const previousEnabledRef = useRef(isRealtimeEnabled);
    useEffect(() => {
        // Só executa se o estado realmente mudou
        if (previousEnabledRef.current === isRealtimeEnabled) {
            return;
        }
        previousEnabledRef.current = isRealtimeEnabled;

        if (isRealtimeEnabled) {
            if (useSSE) {
                connectSSE();
            } else {
                startPollingInternal();
            }
        } else {
            if (useSSE) {
                disconnectSSE();
            } else {
                stopPollingInternal();
            }
        }

        // Cleanup
        return () => {
            if (useSSE) {
                disconnectSSE();
            } else {
                stopPollingInternal();
            }
        };
    }, [isRealtimeEnabled, useSSE, connectSSE, disconnectSSE, startPollingInternal, stopPollingInternal]);

    /**
     * Gerenciar visibilidade da página (pausar quando aba não está visível)
     */
    useEffect(() => {
        if (!pauseOnHidden) return;

        const handleVisibilityChange = () => {
            isPageVisibleRef.current = !document.hidden;
            
            if (isRealtimeEnabled && !useSSE) {
                if (document.hidden) {
                    // Pausar quando aba fica oculta
                    stopPollingInternal();
                } else {
                    // Retomar quando aba fica visível
                    startPollingInternal();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [pauseOnHidden, isRealtimeEnabled, useSSE, startPollingInternal, stopPollingInternal]);

    /**
     * Cleanup ao desmontar
     */
    useEffect(() => {
        return () => {
            stopPollingInternal();
            disconnectSSE();
        };
    }, [stopPollingInternal, disconnectSSE]);

    return {
        isRealtimeEnabled,
        isConnected,
        lastUpdate,
        error,
        enableRealtime,
        disableRealtime,
        toggleRealtime,
    };
};

export default useRealtimeData;

